import isEmpty from 'lodash/isEmpty';
// biome-ignore lint/correctness/noUnusedImports: existing error
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  addCustomRecipientMessage,
  getCustomRecipientsMessage,
} from '~/CreateFabricGreetingCard/api/recipientMessages';
import {selectHasGiftCard} from '~/CreateFabricGreetingCard/reducers/eviteEvent/selectors';
import {
  selectBackgroundColor,
  selectFabricArt,
  selectSelectedBackground,
} from '~/CreateFabricGreetingCard/reducers/fabricDesign/selectors';
import {
  selectGlobalMessage,
  selectMessage,
  selectPersistAllMessages,
  selectRecipientMessages,
  selectRecipientSelected,
  UPDATE_GLOBAL_MESSAGE,
} from '~/CreateFabricGreetingCard/reducers/recipientsMessage';
import {NavBottom} from '~/common/fabric/Card/components/NavBottom/NavBottom';
import {PreviewCard} from '~/common/fabric/Card/components/PreviewCard/PreviewCard';
import {Tooltip} from '~/common/fabric/Card/components/Tooltip/Tooltip';
import {fetchGuestsListThunk} from '~/common/fabric/EventGuests/api/guests';
import {selectGuestById, selectGuests} from '~/common/fabric/EventGuests/reducers/guests/selectors';
import {
  GiftCardBar,
  GiftCardButton,
} from '~/common/fabric/GiftCard/components/GiftCardButton/GiftCardButton';
import {selectSender} from '~/common/fabric/reducers/review/selectors';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {GlobalRecipientsMessage} from './components/GlobalMessage/GlobalMessage';
import {IndividualMessages} from './components/IndividualMessages/IndividualMessages';
import {SingleRecipientPage} from './components/IndividualMessages/SingleRecipientPage';

import './RecipientsMessage.sass';

const PreviewElement = ({setPreview, selectedRecipient, hasGiftCard = false}) => {
  const isDesktop = useIsLargishAndUp();

  const fabricArt = useSelector(selectFabricArt);
  const backgroundColor = useSelector(selectBackgroundColor);
  const background = useSelector(selectSelectedBackground);
  const senderName = useSelector(selectSender);
  const message = useSelector(selectMessage);
  const selectedGuest = useSelector(selectGuestById(selectedRecipient));

  return (
    <PreviewCard
      previewPanelClassName={hasGiftCard ? 'gift-card' : ''}
      background={background}
      backgroundColor={backgroundColor}
      fabricArt={fabricArt}
      message={message}
      selectedGuest={selectedGuest}
      userName={senderName}
      onPreviewBack={() => setPreview(false)}
      isMobile={!isDesktop}
      hasGiftCard={hasGiftCard}
    />
  );
};

export const RecipientsMessage = () => {
  const dispatch = useDispatch();
  const isDesktop = useIsLargishAndUp();
  const pointRef = useRef();
  const containerRef = useRef();
  const [preview, setPreview] = useState(false);
  const globalMessage = useSelector(selectGlobalMessage);
  const {guests} = useSelector(selectGuests);
  const recipientSelected = useSelector(selectRecipientSelected);
  const recipientMessages = useSelector(selectRecipientMessages);
  const persistMessage = useSelector(selectPersistAllMessages);
  const hasGiftCard = useSelector(selectHasGiftCard);

  const checkIfCustomMessage = () => {
    if (!isEmpty(globalMessage)) {
      return true;
    }

    for (const recipient in recipientMessages) {
      if (!isEmpty(recipientMessages[recipient])) {
        return true;
      }
    }

    return false;
  };

  const onGlobalMessageChange = useCallback(
    (newMessage) => {
      dispatch({
        type: UPDATE_GLOBAL_MESSAGE,
        payload: newMessage,
      });
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(fetchGuestsListThunk(null, true));
    dispatch(getCustomRecipientsMessage());
  }, [dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    const updatePointPosition = () => {
      const [selected] = document.getElementsByClassName('individual-message selected');
      const globalMessageElement = document.getElementById('global-message');
      const inputField = globalMessageElement?.lastElementChild;
      const position = selected?.offsetTop || inputField?.offsetTop;
      pointRef.current.style.top = `${position - containerRef.current.scrollTop}px`;
    };

    if (pointRef.current && containerRef.current) {
      updatePointPosition();
      const container = containerRef.current;
      container?.addEventListener('scroll', updatePointPosition);

      return () => {
        container?.removeEventListener('scroll', updatePointPosition);
      };
    }
    return undefined;
  }, [recipientSelected]);

  useEffect(() => {
    if (persistMessage) {
      dispatch(addCustomRecipientMessage(Object.keys(recipientMessages)));
    }
  }, [dispatch, persistMessage, recipientMessages]);

  const scrollToTop = useCallback(() => {
    containerRef?.current?.scrollTo(0, 0);
  }, []);

  return (
    <>
      {isDesktop || preview
        ? <PreviewElement
            setPreview={setPreview}
            hasGiftCard={hasGiftCard}
            selectedRecipient={recipientSelected}
          />
        : null}
      {!isDesktop && preview && hasGiftCard && (
        <GiftCardBar>
          <GiftCardButton text="Open my gift" disabled />
        </GiftCardBar>
      )}
      <div
        id="messages-container"
        className="recipients-message__messages-container create-flow__options"
        style={{display: preview ? 'none' : 'block'}}
      >
        <div className="create-flow__form-container">
          {isDesktop && <div ref={pointRef} className="reference-pointer" id="reference-pointer" />}
          {!!guests?.page?.length && (
            <div
              ref={containerRef}
              id="messages"
              className="recipients-message__messages-container__messages"
            >
              <Tooltip
                id="recipient_message_tooltip"
                text={`Messages added on this step will \ndisplay on the back of your eCard.`}
                dataQaIdExitBtn="exit-shareablelink-message"
              />
              {guests?.page?.length > 1
                ? <>
                    <GlobalRecipientsMessage
                      onMessageChange={onGlobalMessageChange}
                      message={globalMessage}
                    />
                    <IndividualMessages
                      guests={guests}
                      isMobile={!isDesktop}
                      onPreview={() => setPreview(true)}
                      globalMessage={globalMessage}
                      scrollToTop={scrollToTop}
                    />
                  </>
                : <SingleRecipientPage
                    guest={guests.page[0]}
                    isMobile={!isDesktop}
                    onPreview={() => setPreview(true)}
                  />}
            </div>
          )}
          <NavBottom
            ctaVariant={checkIfCustomMessage() ? 'primary' : 'secondary'}
            customNextButtonText={
              checkIfCustomMessage() ? 'Next: Review & send' : 'Skip & check out'
            }
          />
        </div>
      </div>
    </>
  );
};
